import renderAndEnableListingTiles from '../listing_tile_rendering/listingTileRenderer'
import algoliarecommend from '@algolia/recommend'
import { pushToDataLayer } from '../../analytics/PushToDataLayer'
import { buildFilters, getAlgoliaIndexFromUrl, getTable, initiateSimilarSearch } from '../../utils/search/helpers'
import { typesenseEnabled } from '../../libs/search/searchClient'
import { onVisible } from '../utils'

export default function initSimilarRecommendedListingsModule () {
  const recommendSections = document.querySelectorAll('.vjs-algolia-recommends-section')
  recommendSections.forEach(recommendSection => {
    if (recommendSection.dataset.lazyLoaded === 'true') {
      onVisible(recommendSection, loadSimilarRecommendedListings)
    } else {
      loadSimilarRecommendedListings(recommendSection)
    }
  })
}

function loadSimilarRecommendedListings (recommendSection) {
  fetchSimilarListings(recommendSection.dataset).then(results => {
    renderSection(results, recommendSection, true)

    const secondaryRecommendSection = document.getElementById('vjs-similar-listings-section')
    if (secondaryRecommendSection !== recommendSection) {
      renderSection(results, secondaryRecommendSection, false)
    }
  })
}

function fetchSimilarListings (dataset) {
  if (typesenseEnabled()) return fetchTypesenseSimilarListings(dataset)

  const client = algoliarecommend(process.env.ALGOLIASEARCH_APPLICATION_ID, process.env.ALGOLIASEARCH_API_KEY_SEARCH)

  // Algolia Recommend doesn't seem to support clickAnalytics / queryID
  return client.getRecommendations(getAlgoliaRecomendationsConfig(dataset))
}

// Typesense has no counterpart to Algolia Recommend, so approximate with a
// similar-query search on the title (Algolia's own fallbackParameters
// approach). Stand-in until a proper recommendations solution replaces it.
function fetchTypesenseSimilarListings (dataset) {
  const { listingId, listingTitle, productType, subProductType, subcategory } = dataset
  const filters = buildFilters({ listingId, productType, subProductType, subcategory })

  // Reshaped to match getRecommendations so renderSection can stay engine-blind.
  return initiateSimilarSearch(listingTitle, 20, filters).then(results => (
    { results: [{ hits: results.hits }] }
  ))
}

function renderSection (algoliaResults, section, trackImpression, showViewSimilar = true) {
  const { hitsPerPage, customComponentName = '' } = section.dataset

  const hits = algoliaResults.results[0]?.hits.slice(0, hitsPerPage) || []
  const tileOptions = { showViewSimilar }

  if (trackImpression) {
    pushToDataLayer('recommendedListingsImpression',
      getCustomImpressionConfig(hits, customComponentName)
    )
  }

  renderAndEnableListingTiles(section, hits, tileOptions)
  import(/* webpackChunkName: "carousels" */ '../carousels').then(module => module.initPDPViewMoreCarousel(section))
}

function getRecommendationsOptionalFilters () {
  const optionalFilters = getTable('perso-reco-optional-filters')
  if (optionalFilters) return optionalFilters.filter(item => !item.startsWith('category'))
  return []
}

function getAlgoliaRecomendationsConfig (dataset) {
  const {
    listingId, listingTitle, productType, subProductType, subcategory, recommendModel
  } = dataset
  const filters = buildFilters({ listingId, productType, subProductType, subcategory })
  const categoryOptionalFilters = buildOptionalFilters(productType, subProductType, subcategory)

  const affinityOptionalFilters = getRecommendationsOptionalFilters()
  const optionalFilters = affinityOptionalFilters ? categoryOptionalFilters.concat(affinityOptionalFilters) : categoryOptionalFilters

  return [{
    indexName: getAlgoliaIndexFromUrl(),
    objectID: String(listingId),
    model: recommendModel,
    maxRecommendations: 20,
    fallbackParameters: {
      similarQuery: listingTitle,
      filters: filters,
      attributesToRetrieve: ['*', '-description']
    },
    queryParameters: {
      optionalFilters: optionalFilters,
      attributesToRetrieve: ['*', '-description']
    }
  }]
}

function buildOptionalFilters (productType, subProductType, subcategory) {
  const optionalFilters = []

  if (subProductType) optionalFilters.push(`sub_product_type_name:${subProductType}`)
  if (productType) optionalFilters.push(`product_type_name:${productType}`)
  if (subcategory) optionalFilters.push(`subcategory_name:${subcategory}`)

  return optionalFilters
}

// Helper for tracking below
function getCustomImpressionConfig (hits, customSectionName) {
  const recommendedListings = getRecommendedListingsFromHits(hits)
  const filteredScores = recommendedListings
    .filter(listing => listing.listing_score)
    .map(listing => listing.listing_score)
  const numberOfRecommendedListings = filteredScores.length
  const scoreAverage = averageScoresFromRecommendedListings(filteredScores)
  const numberOfFallbackListings = hits.length - numberOfRecommendedListings

  return {
    recommended_listings: recommendedListings,
    average_score: scoreAverage,
    number_of_recommended_listings: numberOfRecommendedListings,
    number_of_fallback_listings: numberOfFallbackListings,
    custom_section_name: customSectionName
  }
}

function getRecommendedListingsFromHits (hits) {
  return hits.map((hit, index) => {
    const { objectID, title, _score } = hit
    const listingPosition = index + 1

    return {
      listing_id: objectID,
      listing_name: title,
      listing_score: _score,
      listing_position: listingPosition
    }
  })
}

function averageScoresFromRecommendedListings (filteredScores) {
  if (filteredScores.length > 0) {
    const scoreSum = filteredScores.reduce((prev, curr) => prev + curr)
    return scoreSum / filteredScores.length
  } else {
    return 0
  }
}
export { renderSection, getAlgoliaRecomendationsConfig }
